<script setup>
import { ref, computed, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { isoRangeForPreset } from '@/composables/date-range-presets'
import { useDetectDevice } from '@/composables/device'
import { useReportColumnTemplates } from '@/composables/report-column-template'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore, infoBoxIdEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import PrintOptionToggles from '@/components/shared/PrintOptionToggles.vue'
import ReportFileDialog from '@/components/accounting/ReportFileDialog.vue'
import ReportFormattedGrid from '@/components/accounting/ReportFormattedGrid.vue'
import ReportMobileSections from '@/components/accounting/ReportMobileSections.vue'
import ReportMobileSummary from '@/components/accounting/ReportMobileSummary.vue'

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { reportTypeEnum, reportSubjectEnum } = accountingStore
const { partsOf, withParts } = useReportColumnTemplates()
const { dateRangePresetEnum } = portalStore

const ALLOWED_PRESETS = Object.freeze([dateRangePresetEnum.WEEK, dateRangePresetEnum.MONTH, dateRangePresetEnum.QUARTER, dateRangePresetEnum.YEAR])

const selectedPartKeys = ref([])
const range = ref(isoRangeForPreset(dateRangePresetEnum.MONTH))
const rendered = ref(null)
const isLoading = ref(false)
const showFile = ref(false)

const partOptions = computed(() => partsOf(reportTypeEnum.STAFF_ACTIVITY).map(part => ({
  key: part.key,
  label: i18n.t(part.label),
  value: selectedPartKeys.value.includes(part.key),
  disable: selectedPartKeys.value.length === 1 && selectedPartKeys.value.includes(part.key),
})))
const params = computed(() => ({
  subject: reportSubjectEnum.STAFF,
  staff: webSiteStore.staffId,
  date_from: range.value.from,
  date_to: range.value.to,
  output_config: withParts({ document_type: reportTypeEnum.STAFF_ACTIVITY, sections: {} }, selectedPartKeys.value),
}))

const loadAsync = async() => {
  if (!webSiteStore.staffId) return
  isLoading.value = true
  rendered.value = await accountingStore.renderReportAsync(params.value).finally(() => { isLoading.value = false })
}

const onPartToggled = (key, value) => {
  selectedPartKeys.value = value ? [...selectedPartKeys.value, key] : selectedPartKeys.value.filter(partKey => partKey !== key)
  loadAsync()
}
const onRangeChanged = (value) => {
  range.value = value
  loadAsync()
}

onMounted(async() => {
  await accountingStore.fetchReportColumnsAsync()
  selectedPartKeys.value = partsOf(reportTypeEnum.STAFF_ACTIVITY).map(part => part.key)
  loadAsync()
})
</script>

<template>
  <div :class="isMobile ? 'my-reports-mobile column no-wrap' : 'q-pa-sm'">
    <q-dialog :model-value="showFile" :maximized="isMobile" @hide="showFile = false">
      <report-file-dialog
        v-if="showFile"
        :params="params"
        @cancel="showFile = false"
      />
    </q-dialog>
    <div v-if="!webSiteStore.staffId" class="empty-container">
      <img src="/img/no-documents.svg" :style="{ maxWidth: '80vw', objectFit: 'contain' }" class="q-mb-xl">
      <p class="text-h5">{{ i18n.t('common.report.noLinkedStaff') }}</p>
      <p class="sub text-weight-light">{{ i18n.t('common.report.noLinkedStaffHint') }}</p>
    </div>
    <template v-else>
      <div class="q-gutter-sm q-mb-sm" :class="isMobile ? 'column q-px-sm q-pt-sm' : 'row items-center'">
        <print-option-toggles :options="partOptions" inline @update="onPartToggled" />
        <div class="row items-center no-wrap" :class="{ 'col': !isMobile }">
          <date-range-navigator
            :model-value="range"
            :default-preset="dateRangePresetEnum.MONTH"
            :allowed-presets="ALLOWED_PRESETS"
            @update:model-value="onRangeChanged"
          />
          <q-space />
          <q-btn
            v-if="isMobile"
            flat
            round
            color="secondary"
            icon="mdi-printer"
            :disable="!rendered"
            @click="showFile = true"
          />
          <q-btn
            v-else
            color="secondary"
            icon="mdi-printer"
            :label="i18n.t('common.report.downloadPdfExcel')"
            :disable="!rendered"
            @click="showFile = true"
          />
        </div>
      </div>
      <q-inner-loading :showing="isLoading" />
      <div v-if="isMobile" class="col" style="min-height: 0;">
        <report-mobile-sections
          v-if="rendered"
          :key="`${selectedPartKeys.join()}-${range.from}-${range.to}`"
          :build="rendered.build"
          :formatted="rendered.formatted"
          :subject="reportSubjectEnum.STAFF"
        >
          <report-mobile-summary :build="rendered.build" />
          <q-separator />
        </report-mobile-sections>
      </div>
      <template v-else>
        <report-formatted-grid
          v-if="rendered"
          :formatted="rendered.formatted"
          height="62vh"
        />
        <info-box :id="infoBoxIdEnum.REPORT_MY_SUMMARIES" class="q-mt-md" :message="i18n.t('common.report.mySummariesInfo')" />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.my-reports-mobile {
  height: calc(100vh - 4rem);
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}
</style>
